import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const normalizeEntityName = (entity) => {
  if (!entity) return '—';
  if (typeof entity === 'string') return entity;
  return entity.name || entity.code || entity._id || '—';
};

const formatDate = (value) => {
  if (!value) return '—';
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
};

const renderValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((item) => normalizeEntityName(item)).join(', ');
  }
  if (typeof value === 'object') return normalizeEntityName(value);
  return String(value);
};

const StorePartsTab = () => {
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedPartId, setSelectedPartId] = useState('');
  const [selectedPart, setSelectedPart] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const selectedResult = useMemo(
    () => results.find((row) => String(row._id) === String(selectedPartId)) || null,
    [results, selectedPartId]
  );

  const searchParts = async () => {
    const term = query.trim();
    if (!term) {
      showToast('Enter part name, part number, or part ID to search', 'warning');
      return;
    }
    setLoadingSearch(true);
    setSelectedPart(null);
    setSelectedPartId('');
    try {
      const res = await axios.get('/api/parts', {
        params: {
          search: term,
          isActive: true,
          page: 1,
          pageSize: 50
        }
      });
      const rows = Array.isArray(res.data?.parts) ? res.data.parts : [];
      setResults(rows);
      if (!rows.length) {
        showToast('No parts found for the entered value', 'info');
      }
    } catch (error) {
      console.error(error);
      showToast(error.response?.data?.message || 'Failed to search parts', 'error');
    } finally {
      setLoadingSearch(false);
    }
  };

  const loadPartDetails = async (partId) => {
    if (!partId) return;
    setLoadingDetails(true);
    try {
      const res = await axios.get(`/api/parts/${partId}`);
      setSelectedPart(res.data || null);
      setSelectedPartId(partId);
    } catch (error) {
      console.error(error);
      showToast(error.response?.data?.message || 'Failed to load part details', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };

  const detailFields = [
    ['Part ID', selectedPart?.partId],
    ['Mongo ID', selectedPart?._id],
    ['Part Number', selectedPart?.partNumber],
    ['Part Name', selectedPart?.partName],
    ['Description', selectedPart?.description],
    ['Category', selectedPart?.category],
    ['Manufacturer(s)', selectedPart?.manufacturers?.length ? selectedPart.manufacturers : selectedPart?.manufacturer],
    ['Part Type', selectedPart?.partType],
    ['Unit Metric', selectedPart?.unitMetric],
    ['Frequency', selectedPart?.frequency],
    ['ATA Chapter', selectedPart?.ataChapter ? `${selectedPart.ataChapter.code || ''} ${selectedPart.ataChapter.name || ''}`.trim() : null],
    ['Primary Models', selectedPart?.primaryModels?.length ? selectedPart.primaryModels : selectedPart?.model],
    ['Model Variants', selectedPart?.modelVariants],
    ['Applicable Assemblies', selectedPart?.applicableAssemblyTypes],
    ['Serial Number Required', selectedPart?.serialNumberRequired],
    ['Serial Number(s)', selectedPart?.serialNumbers?.length ? selectedPart.serialNumbers : selectedPart?.serialNumber],
    ['Reorder Required', selectedPart?.reOrderLevelRequired],
    ['Reorder Level', selectedPart?.reOrderLevel],
    ['Min / Max Level', selectedPart ? `${selectedPart.minimumLevel ?? '—'} / ${selectedPart.maximumLevel ?? '—'}` : null],
    ['Expiry Item', selectedPart?.expiryItem],
    ['Expiry Type', selectedPart?.expiryType],
    ['Expiry Date', formatDate(selectedPart?.expirySpecificDate)],
    ['Approximate Rate', selectedPart?.approximateRate],
    ['Note', selectedPart?.note],
    ['Inventory Total Qty', selectedPart?.inventoryStatus?.totalQuantity],
    ['Part Active', selectedPart?.isActive]
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Parts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search by part name, part number, or part ID, then open a result to view complete details.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={9}>
            <TextField
              fullWidth
              size="small"
              label="Part Name / Part Number / Part ID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') searchParts();
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={searchParts} disabled={loadingSearch}>
              {loadingSearch ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loadingSearch ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Part ID</TableCell>
                <TableCell>Part Number</TableCell>
                <TableCell>Part Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!results.length ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Search to view parts.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                results.map((part) => (
                  <TableRow key={part._id} selected={String(selectedPartId) === String(part._id)}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{part.partId || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{part.partNumber || '—'}</TableCell>
                    <TableCell>{part.partName || '—'}</TableCell>
                    <TableCell>{part.description || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" color={part.isActive ? 'success' : 'default'} label={part.isActive ? 'Active' : 'Inactive'} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => loadPartDetails(part._id)}>
                        View details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Complete Part Details
          {selectedResult ? `: ${selectedResult.partNumber || selectedResult.partId || selectedResult._id}` : ''}
        </Typography>
        {loadingDetails ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        ) : !selectedPart ? (
          <Alert severity="info">Select a part from search results to view complete details.</Alert>
        ) : (
          <Grid container spacing={2}>
            {detailFields.map(([label, value]) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2">{renderValue(value)}</Typography>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default StorePartsTab;
